import { Constants } from "@eagerdog/constants";

/**
 * Social feed notifications (data/prd/SOCIAL_NOTIFICATIONS_PRD.md).
 *
 * One kind per user action. The same payload is sent by email (Knock) and
 * push (Expo); the kind decides the Knock workflow and the preference
 * category that can switch it off.
 */
export type SocialNotificationKind =
  (typeof Constants.social_notification_kind)[keyof typeof Constants.social_notification_kind];

export type SocialNotificationCategory =
  (typeof Constants.social_notification_category)[keyof typeof Constants.social_notification_category];

export type NotificationChannel =
  (typeof Constants.notification_channel)[keyof typeof Constants.notification_channel];

/** Per-category, per-channel switches. Missing → on. */
export type INotificationPreferences = Record<
  SocialNotificationCategory,
  Record<NotificationChannel, boolean>
>;

/** Bookkeeping for one-shot reminders. */
export interface INotificationState {
  what_you_missed_sent_at?: Date;
}

export const SOCIAL_NOTIFICATION_CATEGORY_BY_KIND: Record<SocialNotificationKind, SocialNotificationCategory> = {
  [Constants.social_notification_kind.post_liked]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.post_shared]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.title_earned_own]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.result_entered_own]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.dog_followed]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.club_followed]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.event_published_share]: Constants.social_notification_category.activity,
  [Constants.social_notification_kind.followed_dog_title]: Constants.social_notification_category.following,
  [Constants.social_notification_kind.followed_dog_post]: Constants.social_notification_category.following,
  [Constants.social_notification_kind.followed_club_post]: Constants.social_notification_category.following,
  [Constants.social_notification_kind.what_you_missed]: Constants.social_notification_category.reminders,
  [Constants.social_notification_kind.share_your_run]: Constants.social_notification_category.reminders,
};

/**
 * Knock workflow key per kind. Batching lives on the workflow:
 * post-liked batches 1 h on `post_id`; followed-dog/club-post batch 24 h per
 * recipient; event-published-share has a delay step and is cancelled when
 * the club customises the post.
 */
export const SOCIAL_NOTIFICATION_WORKFLOW_KEY: Record<SocialNotificationKind, string> = {
  [Constants.social_notification_kind.post_liked]: "social-post-liked",
  [Constants.social_notification_kind.post_shared]: "social-post-shared",
  [Constants.social_notification_kind.title_earned_own]: "social-title-earned",
  [Constants.social_notification_kind.result_entered_own]: "social-result-entered",
  [Constants.social_notification_kind.dog_followed]: "social-dog-followed",
  [Constants.social_notification_kind.club_followed]: "social-club-followed",
  [Constants.social_notification_kind.event_published_share]: "social-event-published-share",
  [Constants.social_notification_kind.followed_dog_title]: "social-followed-dog-title",
  [Constants.social_notification_kind.followed_dog_post]: "social-followed-dog-post",
  [Constants.social_notification_kind.followed_club_post]: "social-followed-club-post",
  [Constants.social_notification_kind.what_you_missed]: "social-what-you-missed",
  [Constants.social_notification_kind.share_your_run]: "social-share-your-run",
};

/** Kinds that are batched in Knock and therefore skip push (Expo has no batching). */
export const SOCIAL_NOTIFICATION_PUSH_KINDS: ReadonlySet<SocialNotificationKind> = new Set<SocialNotificationKind>([
  Constants.social_notification_kind.post_liked,
  Constants.social_notification_kind.post_shared,
  Constants.social_notification_kind.title_earned_own,
  Constants.social_notification_kind.result_entered_own,
  Constants.social_notification_kind.dog_followed,
  Constants.social_notification_kind.club_followed,
  Constants.social_notification_kind.followed_dog_title,
]);

export const DEFAULT_NOTIFICATION_PREFERENCES: INotificationPreferences = {
  [Constants.social_notification_category.activity]: { email: true, push: true },
  [Constants.social_notification_category.following]: { email: true, push: true },
  [Constants.social_notification_category.reminders]: { email: true, push: true },
};

/** Fill gaps so callers can read `prefs[category][channel]` without guards. */
export function resolveNotificationPreferences(
  prefs?: Partial<INotificationPreferences> | null
): INotificationPreferences {
  const out = {} as INotificationPreferences;
  for (const category of Object.values(Constants.social_notification_category)) {
    out[category] = {
      email: prefs?.[category]?.email ?? true,
      push: prefs?.[category]?.push ?? true,
    };
  }
  return out;
}

/** A linked line in a reminder email (what you missed / share your run). */
export interface ISocialNotificationItem {
  headline: string;
  url: string;
  image_url?: string;
}

/**
 * Payload for every social email. The Knock templates share this shape so a
 * new kind needs a workflow, not new plumbing.
 */
export interface IEmailSocialNotification {
  kind: SocialNotificationKind;

  user_id: string;
  email: string;
  display_name: string;

  subject: string;
  /** One bold line: "Tango earned the RN title". */
  headline: string;
  /** One supporting sentence. */
  body?: string;
  cta_label: string;
  /** Absolute URL into the product. */
  url: string;
  image_url?: string;

  /** Who did it (liked, followed, shared). */
  actor_name?: string;
  /** The dog, club or event the notification is about. */
  subject_name?: string;
  /** Batch key for post-liked. */
  post_id?: string;
  items?: ISocialNotificationItem[];
  /** Plain detail lines rendered as a card under the headline (result emails: sport, class, where, stats, judge). */
  details?: string[];

  preferences_url: string;
  unsubscribe_url: string;
  /** Knock cancellation key for delayed workflows. */
  cancellation_key?: string;
}

/** Cancel a delayed social workflow run (event_published_share). */
export interface ISocialNotificationCancel {
  kind: SocialNotificationKind;
  cancellation_key: string;
  user_ids: string[];
}

/** Branded replacement for the removed-post email (CONTENT_MODERATION_PRD). */
export interface IEmailPostRemovedNotification {
  user_id: string;
  email: string;
  display_name: string;
  post_description: string;
  reason_label: string;
  suspended_until_formatted?: string;
  guidelines_url: string;
}
